// src/pages/Albaranes.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Topbar from "../components/Topbar";
import Breadcrumb from "../components/Breadcrumb";
import ModalConfirmacion from "../components/ModalConfirmacion";

import "../styles/Albaranes.css";

export default function Albaranes() {

  const token = localStorage.getItem("token");

  const [albaranes, setAlbaranes] = useState([]);
  const [pacientes, setPacientes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingPacientes, setLoadingPacientes] = useState(false);

  const [nuevaFila, setNuevaFila] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [filaEditada, setFilaEditada] = useState(null);

  const [modalEliminar, setModalEliminar] = useState(false);
  const [albaranEliminar, setAlbaranEliminar] = useState(null);

  const hoy = new Date().toISOString().slice(0, 10);

  const [filtros, setFiltros] = useState({
    fechaDesde: hoy,
    fechaHasta: hoy,
    clinica: "",
    doctor: ""
  });

  const profesionalesOptions = [
    { value: "Paola", label: "Paola" },
    { value: "Maria", label: "Maria" },
    { value: "Lucia", label: "Lucia" },
    { value: "Lis OR", label: "Lis OR" },
    { value: "Luis CR", label: "Luis CR" },
    { value: "Aldana", label: "Aldana" },
    { value: "Estefania", label: "Estefanía" },
    { value: "Marines", label: "Marinés" }
  ];

  const clinicasOptions = [
    { value: "LA ELIPA", label: "ELIPA" },
    { value: "VEN", label: "VEN" }
  ];

  /*
   * =========================
   * PACIENTES
   * =========================
   */

  const cargarPacientes = async () => {
    setLoadingPacientes(true);

    try {
      const { data } = await axios.get("/api/pacientes", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      /*
       * Por si el endpoint alguna vez devuelve paginación,
       * admitimos tanto array directo como data.content.
       */
      const lista = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : [];

      setPacientes(lista);

    } catch (error) {
      console.error("Error al cargar pacientes:", error.response || error);
      toast.error("Error al cargar los pacientes");
    } finally {
      setLoadingPacientes(false);
    }
  };

  const pacientesOptions = useMemo(() => {

    return pacientes.map(p => {

      const expediente =
        p.n_expediente ??
        "";

      const nombreCompleto = [
        p.nombre,
        p.primer_apellido ?? p.primerApellido,
        p.segundo_apellido ?? p.segundoApellido
      ]
        .filter(Boolean)
        .join(" ");

      return {
        value: Number(expediente),
        label: `${expediente} - ${nombreCompleto}`,
        nombreCompleto
      };
    });

  }, [pacientes]);

  /*
   * =========================
   * ALBARANES
   * =========================
   */

  const cargarAlbaranes = async (filtrosAplicados = filtros) => {
    setLoading(true);

    try {

      const params = Object.fromEntries(
        Object.entries(filtrosAplicados)
          .filter(([_, valor]) =>
            valor !== "" &&
            valor !== null &&
            valor !== undefined
          )
      );

      const { data } = await axios.get("/api/albaranes", {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setAlbaranes(Array.isArray(data) ? data : []);

    } catch (error) {
      console.error(
        "Error al cargar albaranes:",
        error.response || error
      );

      toast.error("Error al cargar los albaranes");

    } finally {
      setLoading(false);
    }
  };

  /*
   * =========================
   * CARGA INICIAL
   * =========================
   */

  useEffect(() => {
    cargarPacientes();
    cargarAlbaranes({
      fechaDesde: hoy,
      fechaHasta: hoy,
      clinica: "",
      doctor: ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * =========================
   * FILTROS
   * =========================
   */

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;

    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const buscar = () => {
    cargarAlbaranes();
  };

  const limpiarFiltros = () => {

    const filtrosLimpios = {
      fechaDesde: "",
      fechaHasta: "",
      clinica: "",
      doctor: ""
    };

    setFiltros(filtrosLimpios);

    cargarAlbaranes(filtrosLimpios);
  };

  /*
   * =========================
   * NUEVO ALBARÁN
   * =========================
   */

  const iniciarNuevo = () => {

    if (editandoId !== null) {
      toast.warning(
        "Finaliza primero la edición del albarán actual"
      );
      return;
    }

    setNuevaFila({
      fecha: new Date().toISOString().slice(0, 10),
      n_expediente: null,
      clinica: "",
      importe: "",
      doctor: ""
    });
  };

  const cancelarNuevo = () => {
    setNuevaFila(null);
  };

  const guardarNuevo = async () => {

    if (!nuevaFila.fecha) {
      toast.error("Debes indicar una fecha");
      return;
    }

    if (!nuevaFila.n_expediente) {
      toast.error("Debes seleccionar un paciente");
      return;
    }

    if (!nuevaFila.clinica) {
      toast.error("Debes seleccionar una clínica");
      return;
    }

    if (
      nuevaFila.importe === "" ||
      nuevaFila.importe === null ||
      Number(nuevaFila.importe) < 0
    ) {
      toast.error("Debes indicar un importe válido");
      return;
    }

    if (!nuevaFila.doctor) {
      toast.error("Debes seleccionar un doctor");
      return;
    }

    try {

      const payload = {
        fecha: nuevaFila.fecha,
        n_expediente: Number(nuevaFila.n_expediente),
        clinica: nuevaFila.clinica,
        importe: Number(nuevaFila.importe),
        doctor: nuevaFila.doctor
      };

      await axios.post(
        "/api/albaranes",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Albarán guardado correctamente");

      setNuevaFila(null);

      await cargarAlbaranes();

    } catch (error) {

      console.error(
        "Error guardando albarán:",
        error.response || error
      );

      toast.error("Error al guardar el albarán");
    }
  };

  /*
   * =========================
   * EDITAR
   * =========================
   */

  const iniciarEdicion = (albaran) => {

    if (nuevaFila) {
      toast.warning(
        "Guarda o cancela primero el nuevo albarán"
      );
      return;
    }

    setEditandoId(albaran.id);

    setFilaEditada({
      ...albaran,
      
      n_expediente:
        albaran.n_expediente ??
        null,

      importe:
        albaran.importe !== null &&
        albaran.importe !== undefined
          ? albaran.importe
          : ""
    });
  };

  const actualizarFilaEditada = (campo, valor) => {

    setFilaEditada(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setFilaEditada(null);
  };

  const guardarEdicion = async () => {

    if (!filaEditada.fecha) {
      toast.error("Debes indicar una fecha");
      return;
    }

    if (!filaEditada.n_expediente) {
      toast.error("Debes seleccionar un paciente");
      return;
    }

    if (!filaEditada.clinica) {
      toast.error("Debes seleccionar una clínica");
      return;
    }

    if (
      filaEditada.importe === "" ||
      filaEditada.importe === null ||
      Number(filaEditada.importe) < 0
    ) {
      toast.error("Debes indicar un importe válido");
      return;
    }

    if (!filaEditada.doctor) {
      toast.error("Debes seleccionar un doctor");
      return;
    }

    try {

      const payload = {
        fecha: filaEditada.fecha,
        n_expediente: Number(filaEditada.n_expediente),
        clinica: filaEditada.clinica,
        importe: Number(filaEditada.importe),
        doctor: filaEditada.doctor
      };

      await axios.put(
        `/api/albaranes/${editandoId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Albarán actualizado correctamente");

      setEditandoId(null);
      setFilaEditada(null);

      await cargarAlbaranes();

    } catch (error) {

      console.error(
        "Error actualizando albarán:",
        error.response || error
      );

      toast.error("Error al actualizar el albarán");
    }
  };

  /*
   * =========================
   * ELIMINAR
   * =========================
   */

  const solicitarEliminar = (albaran) => {
    setAlbaranEliminar(albaran);
    setModalEliminar(true);
  };

  const eliminarAlbaran = async () => {

    if (!albaranEliminar) return;

    try {

      await axios.delete(
        `/api/albaranes/${albaranEliminar.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success("Albarán eliminado correctamente");

      setModalEliminar(false);
      setAlbaranEliminar(null);

      await cargarAlbaranes();

    } catch (error) {

      console.error(
        "Error eliminando albarán:",
        error.response || error
      );

      toast.error("Error al eliminar el albarán");
    }
  };

  /*
   * =========================
   * TOTAL
   * =========================
   */

  const totalImporte = useMemo(() => {

    return albaranes.reduce(
      (total, albaran) =>
        total + Number(albaran.importe || 0),
      0
    );

  }, [albaranes]);

  /*
   * =========================
   * FORMATO
   * =========================
   */

  const formatearFecha = (fecha) => {

    if (!fecha) return "";

    const fechaLimpia = fecha.substring(0, 10);
    const partes = fechaLimpia.split("-");

    if (partes.length !== 3) return fecha;

    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  };

  const obtenerNombrePaciente = (albaran) => {

    const nombre = [
      albaran.nombre,
      albaran.primerApellido ??
        albaran.primer_apellido,
      albaran.segundoApellido ??
        albaran.segundo_apellido
    ]
      .filter(Boolean)
      .join(" ");

    return nombre || "";
  };

  const nombreProfesional = (valor) => {

    const profesional =
      profesionalesOptions.find(
        p => p.value === valor
      );

    return profesional
      ? profesional.label
      : valor || "";
  };

  /*
   * =========================
   * EXCEL
   * =========================
   */

  const exportarExcel = () => {

    if (!albaranes.length) {
      toast.warning(
        "No hay albaranes para exportar"
      );
      return;
    }

    const datosExportar = albaranes.map(a => ({
      Fecha: formatearFecha(a.fecha),

      Paciente: obtenerNombrePaciente(a),

      Clínica:
        a.clinica === "LA ELIPA"
          ? "ELIPA"
          : a.clinica,

      Importe: Number(a.importe || 0),

      Doctor: nombreProfesional(a.doctor)
    }));

    /*
     * Añadimos el total al final del Excel.
     */
    datosExportar.push({
      Fecha: "",
      Paciente: "",
      Clínica: "TOTAL",
      Importe: totalImporte,
      Doctor: ""
    });

    const hoja =
      XLSX.utils.json_to_sheet(datosExportar);

    /*
     * Anchuras de columnas.
     */
    hoja["!cols"] = [
      { wch: 14 },
      { wch: 40 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 }
    ];

    const libro = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      libro,
      hoja,
      "Albaranes"
    );

    const buffer = XLSX.write(libro, {
      bookType: "xlsx",
      type: "array"
    });

    const blob = new Blob(
      [buffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }
    );

    saveAs(blob, "albaranes.xlsx");
  };

  /*
   * =========================
   * ESTILOS REACT SELECT
   * =========================
   */

  const pacienteSelectStyles = {

    container: base => ({
      ...base,
      minWidth: "320px",
      width: "100%"
    }),

    control: base => ({
      ...base,
      minHeight: "36px",
      fontSize: "14px"
    }),

    menuPortal: base => ({
      ...base,
      zIndex: 9999
    }),

    menu: base => ({
      ...base,
      zIndex: 9999
    })
  };

  /*
   * =========================
   * RENDER
   * =========================
   */

  return (
    <>
      <ToastContainer />
      <div className="albaranes-page">

        <Breadcrumb />

        <div className="albaranes-header">
          <div>
            <h2>Albaranes</h2>

            <p>
              Registro y consulta de albaranes
            </p>
          </div>
        </div>

        {/* =========================
            FILTROS
        ========================= */}

        <div className="albaranes-filtros">

          <div className="albaranes-filtro">

            <label>Fecha desde</label>

            <input
              type="date"
              name="fechaDesde"
              value={filtros.fechaDesde}
              onChange={handleFiltroChange}
            />

          </div>

          <div className="albaranes-filtro">

            <label>Fecha hasta</label>

            <input
              type="date"
              name="fechaHasta"
              value={filtros.fechaHasta}
              onChange={handleFiltroChange}
            />

          </div>

          <div className="albaranes-filtro">

            <label>Clínica</label>

            <select
              name="clinica"
              value={filtros.clinica}
              onChange={handleFiltroChange}
            >
              <option value="">
                Todas
              </option>

              {clinicasOptions.map(c => (
                <option
                  key={c.value}
                  value={c.value}
                >
                  {c.label}
                </option>
              ))}

            </select>

          </div>

          <div className="albaranes-filtro">

            <label>Doctor</label>

            <select
              name="doctor"
              value={filtros.doctor}
              onChange={handleFiltroChange}
            >
              <option value="">
                Todos
              </option>

              {profesionalesOptions.map(p => (
                <option
                  key={p.value}
                  value={p.value}
                >
                  {p.label}
                </option>
              ))}

            </select>

          </div>

          <div className="albaranes-filtros-botones">

            <button
              className="albaranes-btn buscar"
              onClick={buscar}
            >
              Buscar
            </button>

            <button
              className="albaranes-btn limpiar"
              onClick={limpiarFiltros}
            >
              Limpiar
            </button>

          </div>

        </div>

        {/* =========================
            BOTONES
        ========================= */}

        <div className="albaranes-acciones">

          <button
            className="accion-btn guardar"
            onClick={iniciarNuevo}
            disabled={nuevaFila !== null}
          >
            Añadir
          </button>

          <button
            className="accion-btn excel"
            onClick={exportarExcel}
          >
            Descargar Excel
          </button>

        </div>

        {/* =========================
            TABLA
        ========================= */}

        <div className="albaranes-tabla-wrapper">

          {loading ? (

            <div className="albaranes-loading">
              <div className="spinner"></div>
              <p>Cargando albaranes...</p>
            </div>

          ) : (

            <table className="albaranes-table">

              <thead>

                <tr>
                  <th>Fecha</th>
                  <th>Paciente</th>
                  <th>Clínica</th>
                  <th>Importe</th>
                  <th>Doctor</th>
                  <th>Acciones</th>
                </tr>

              </thead>

              <tbody>

                {/* =========================
                    NUEVA FILA
                ========================= */}

                {nuevaFila && (

                  <tr className="fila-edicion fila-nueva">

                    <td>

                      <input
                        type="date"
                        className="input-campo"
                        value={nuevaFila.fecha}
                        onChange={e =>
                          setNuevaFila(prev => ({
                            ...prev,
                            fecha: e.target.value
                          }))
                        }
                      />

                    </td>

                    <td className="columna-paciente">

                      <Select
                        classNamePrefix="albaran-select"
                        options={pacientesOptions}
                        value={
                          pacientesOptions.find(
                            p =>
                              Number(p.value) ===
                              Number(
                                nuevaFila.n_expediente
                              )
                          ) || null
                        }
                        onChange={opcion =>
                          setNuevaFila(prev => ({
                            ...prev,
                            n_expediente:
                              opcion
                                ? opcion.value
                                : null
                          }))
                        }
                        placeholder={
                          loadingPacientes
                            ? "Cargando pacientes..."
                            : "Buscar paciente..."
                        }
                        isClearable
                        isSearchable
                        isDisabled={loadingPacientes}
                        menuPortalTarget={document.body}
                        styles={pacienteSelectStyles}
                      />

                    </td>

                    <td>

                      <select
                        className="input-campo"
                        value={nuevaFila.clinica}
                        onChange={e =>
                          setNuevaFila(prev => ({
                            ...prev,
                            clinica: e.target.value
                          }))
                        }
                      >
                        <option value="">
                          Seleccionar
                        </option>

                        {clinicasOptions.map(c => (
                          <option
                            key={c.value}
                            value={c.value}
                          >
                            {c.label}
                          </option>
                        ))}

                      </select>

                    </td>

                    <td>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input-campo importe-input"
                        value={nuevaFila.importe}
                        onChange={e =>
                          setNuevaFila(prev => ({
                            ...prev,
                            importe: e.target.value
                          }))
                        }
                      />

                    </td>

                    <td>

                      <select
                        className="input-campo"
                        value={nuevaFila.doctor}
                        onChange={e =>
                          setNuevaFila(prev => ({
                            ...prev,
                            doctor: e.target.value
                          }))
                        }
                      >
                        <option value="">
                          Seleccionar
                        </option>

                        {profesionalesOptions.map(p => (
                          <option
                            key={p.value}
                            value={p.value}
                          >
                            {p.label}
                          </option>
                        ))}

                      </select>

                    </td>

                    <td>

                      <div className="acciones-fila">

                        <button
                          className="btn-fila guardar-fila"
                          onClick={guardarNuevo}
                        >
                          Guardar
                        </button>

                        <button
                          className="btn-fila cancelar-fila"
                          onClick={cancelarNuevo}
                        >
                          Cancelar
                        </button>

                      </div>

                    </td>

                  </tr>

                )}

                {/* =========================
                    FILAS EXISTENTES
                ========================= */}

                {albaranes.map(albaran => {

                  const editando =
                    editandoId === albaran.id;

                  if (editando) {

                    return (

                      <tr
                        key={albaran.id}
                        className="fila-edicion"
                      >

                        <td>

                          <input
                            type="date"
                            className="input-campo"
                            value={
                              filaEditada?.fecha
                                ? filaEditada.fecha.substring(
                                    0,
                                    10
                                  )
                                : ""
                            }
                            onChange={e =>
                              actualizarFilaEditada(
                                "fecha",
                                e.target.value
                              )
                            }
                          />

                        </td>

                        <td className="columna-paciente">

                          <Select
                            classNamePrefix="albaran-select"
                            options={pacientesOptions}
                            value={
                              pacientesOptions.find(
                                p =>
                                  Number(p.value) ===
                                  Number(
                                    filaEditada
                                      ?.n_expediente
                                  )
                              ) || null
                            }
                            onChange={opcion =>
                              actualizarFilaEditada(
                                "n_expediente",
                                opcion
                                  ? opcion.value
                                  : null
                              )
                            }
                            placeholder="Buscar paciente..."
                            isClearable
                            isSearchable
                            menuPortalTarget={
                              document.body
                            }
                            styles={
                              pacienteSelectStyles
                            }
                          />

                        </td>

                        <td>

                          <select
                            className="input-campo"
                            value={
                              filaEditada?.clinica ||
                              ""
                            }
                            onChange={e =>
                              actualizarFilaEditada(
                                "clinica",
                                e.target.value
                              )
                            }
                          >
                            <option value="">
                              Seleccionar
                            </option>

                            {clinicasOptions.map(c => (
                              <option
                                key={c.value}
                                value={c.value}
                              >
                                {c.label}
                              </option>
                            ))}

                          </select>

                        </td>

                        <td>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-campo importe-input"
                            value={
                              filaEditada?.importe ??
                              ""
                            }
                            onChange={e =>
                              actualizarFilaEditada(
                                "importe",
                                e.target.value
                              )
                            }
                          />

                        </td>

                        <td>

                          <select
                            className="input-campo"
                            value={
                              filaEditada?.doctor ||
                              ""
                            }
                            onChange={e =>
                              actualizarFilaEditada(
                                "doctor",
                                e.target.value
                              )
                            }
                          >
                            <option value="">
                              Seleccionar
                            </option>

                            {profesionalesOptions.map(p => (
                              <option
                                key={p.value}
                                value={p.value}
                              >
                                {p.label}
                              </option>
                            ))}

                          </select>

                        </td>

                        <td>

                          <div className="acciones-fila">

                            <button
                              className="btn-fila guardar-fila"
                              onClick={guardarEdicion}
                            >
                              Guardar
                            </button>

                            <button
                              className="btn-fila cancelar-fila"
                              onClick={cancelarEdicion}
                            >
                              Cancelar
                            </button>

                          </div>

                        </td>

                      </tr>

                    );
                  }

                  return (

                    <tr key={albaran.id}>

                      <td>
                        {formatearFecha(
                          albaran.fecha
                        )}
                      </td>

                      <td className="paciente-texto">

                        {obtenerNombrePaciente(
                          albaran
                        )}

                      </td>

                      <td>

                        {albaran.clinica ===
                        "LA ELIPA"
                          ? "ELIPA"
                          : albaran.clinica}

                      </td>

                      <td className="importe-columna">

                        {Number(
                          albaran.importe || 0
                        ).toFixed(2)}{" "}
                        €

                      </td>

                      <td>
                        {nombreProfesional(
                          albaran.doctor
                        )}
                      </td>

                      <td>

                        <div className="acciones-fila">

                          <button
                            className="btn-fila editar-fila"
                            onClick={() =>
                              iniciarEdicion(
                                albaran
                              )
                            }
                          >
                            Editar
                          </button>

                          <button
                            className="btn-fila eliminar-fila"
                            onClick={() =>
                              solicitarEliminar(
                                albaran
                              )
                            }
                          >
                            Eliminar
                          </button>

                        </div>

                      </td>

                    </tr>

                  );

                })}

                {!nuevaFila &&
                  albaranes.length === 0 && (

                    <tr>

                      <td
                        colSpan="6"
                        className="sin-resultados"
                      >
                        No hay albaranes para los
                        filtros seleccionados
                      </td>

                    </tr>

                  )}

              </tbody>

              <tfoot>

                <tr>

                  <td
                    colSpan="3"
                    className="total-label"
                  >
                    Total:
                  </td>

                  <td className="total-importe">

                    {totalImporte.toFixed(2)} €

                  </td>

                  <td colSpan="2"></td>

                </tr>

              </tfoot>

            </table>

          )}

        </div>

      </div>

      <ModalConfirmacion
        abierto={modalEliminar}
        mensaje="¿Seguro que deseas eliminar este albarán?"
        textoConfirmar="Eliminar"
        onConfirmar={eliminarAlbaran}
        onCancelar={() => {
          setModalEliminar(false);
          setAlbaranEliminar(null);
        }}
      />

    </>
  );
}